import { useState, useMemo, useCallback } from 'react'
import { Pin, ChevronDown, ChevronUp, X } from 'lucide-react'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'

import { cn } from '@/common/lib/utils'
import { Button } from '@/common/ui/button'
import { useQueryPinnedMessages, useMutationPinMessage } from '../../api/hooks'
import { Skeleton } from '@/common/ui/skeleton'

import type { components } from '@/schema'

type ChatMessageResponse = components['schemas']['ChatMessageResponse']

function getMessagePreview(message: ChatMessageResponse): string {
	switch (message.messageType) {
		case 'TEXT':
			return message.text?.slice(0, 60) || ''
		case 'STICKER':
			return '[สติกเกอร์]'
		case 'IMAGE':
			return '[รูปภาพ]'
		case 'VIDEO':
			return '[วิดีโอ]'
		case 'AUDIO':
			return '[เสียง]'
		case 'FILE':
			return `[ไฟล์] ${message.fileName || ''}`
		case 'LOCATION':
			return '[ตำแหน่ง]'
		default:
			return '[ข้อความ]'
	}
}

function getSenderName(message: ChatMessageResponse): string {
	if (message.direction === 'INCOMING') {
		return 'ลูกค้า'
	}
	return message.sentByOperator?.username || 'แอดมิน'
}

interface PinnedMessagesBannerProps {
	roomId: string
	onClose?: () => void
	avatarUrl?: string
	avatarName?: string
	onNavigateToMessage?: (messageId: string) => void
}

export function PinnedMessagesBanner({
	roomId,
	onNavigateToMessage,
}: PinnedMessagesBannerProps) {
	const [isExpanded, setIsExpanded] = useState(false)
	const { data: pinnedMessages, isLoading } = useQueryPinnedMessages(roomId)
	const pinMessage = useMutationPinMessage(roomId)

	// Get the first pinned message for collapsed view (latest pin)
	const firstPinnedMessage = useMemo(() => {
		if (!pinnedMessages || pinnedMessages.length === 0) return null
		return pinnedMessages[0]
	}, [pinnedMessages])

	const handleMessageClick = useCallback(
		(messageId: string) => {
			if (onNavigateToMessage) {
				onNavigateToMessage(messageId)
				setIsExpanded(false)
			}
		},
		[onNavigateToMessage],
	)

	const toggleExpanded = useCallback(() => {
		setIsExpanded((prev) => !prev)
	}, [])

	if (isLoading) {
		return (
			<div className="border-b bg-muted/50 p-2">
				<div className="flex items-center gap-2">
					<Skeleton className="h-4 w-4" />
					<Skeleton className="h-4 w-20" />
				</div>
			</div>
		)
	}

	if (!pinnedMessages || pinnedMessages.length === 0) {
		return null
	}

	const handleUnpin = (e: React.MouseEvent, messageId: string) => {
		e.stopPropagation()
		pinMessage.mutate({ messageId, isPinned: false })
	}

	// Collapsed state - show only first pinned message (latest pin)
	if (!isExpanded) {
		return (
			<button
				type="button"
				className="flex w-full cursor-pointer items-center gap-2 border-b bg-muted/50 px-3 py-2 text-left transition-colors hover:bg-muted/70"
				onClick={toggleExpanded}
			>
				<Pin className="h-4 w-4 shrink-0 text-primary" />
				<p className="min-w-0 flex-1 truncate text-sm">
					{firstPinnedMessage && getMessagePreview(firstPinnedMessage)}
				</p>
				{pinnedMessages.length > 1 && (
					<span className="shrink-0 text-xs text-muted-foreground">
						+{pinnedMessages.length - 1}
					</span>
				)}
				<ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
			</button>
		)
	}

	// Expanded state - show all pinned messages with simple UI
	return (
		<div className="border-b bg-muted/50">
			{/* Header */}
			<div className="flex items-center justify-between px-3 py-2">
				<div className="flex items-center gap-2">
					<Pin className="h-4 w-4 text-primary" />
					<span className="text-sm font-medium">
						ข้อความที่ปักหมุด ({pinnedMessages.length})
					</span>
				</div>
				<Button
					variant="ghost"
					size="sm"
					onClick={toggleExpanded}
					className="h-7 gap-1 px-2"
				>
					<ChevronUp className="h-4 w-4" />
					<span className="text-xs">ย่อ</span>
				</Button>
			</div>

			{/* Pinned messages list - simple UI */}
			<div className="max-h-[250px] overflow-y-auto px-3 pb-3">
				<div className="space-y-1">
					{pinnedMessages.map((message) => {
						const time = format(new Date(message.createdAt), 'HH:mm', {
							locale: th,
						})
						return (
							<div
								key={message.id}
								className={cn(
									'group flex items-center gap-2 rounded-lg bg-background px-3 py-2',
									onNavigateToMessage &&
										'cursor-pointer transition-colors hover:bg-muted/50',
								)}
								onClick={() => handleMessageClick(message.id)}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault()
										handleMessageClick(message.id)
									}
								}}
								role="button"
								tabIndex={0}
							>
								{/* Message content */}
								<div className="min-w-0 flex-1">
									<p className="truncate text-sm">
										{getMessagePreview(message)}
									</p>
									<p className="text-xs text-muted-foreground">
										โดย {getSenderName(message)} · {time}
									</p>
								</div>

								{/* Unpin button */}
								<Button
									variant="ghost"
									size="icon"
									className="h-7 w-7 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
									onClick={(e) => handleUnpin(e, message.id)}
									disabled={pinMessage.isPending}
								>
									<X className="h-4 w-4" />
								</Button>
							</div>
						)
					})}
				</div>
			</div>
		</div>
	)
}
